/**
 * Chat Service TypeScript Types
 * Based on Chat API Guide (docs/api/CHAT_API_GUIDE.md)
 */

// ============================================================================
// Pagination Types
// ============================================================================

export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  oldest_message_id: string | null;
  newest_message_id: string | null;
}

export interface MessageListResponse {
  messages: Message[];
  pagination: PaginationMeta;
}

export interface MessageQueryParams {
  limit?: number;
  offset?: number;
  before_message_id?: string;
  after_message_id?: string;
  include_archived?: boolean;
}

// ============================================================================
// Chat Types
// ============================================================================

/**
 * Chat Type enum
 * - my_notes: Personal notepad (1 participant)
 * - one_to_one: Direct message (2 participants, recycled)
 * - group: Group chat (1+ participants, with roles)
 */
export enum ChatType {
  MY_NOTES = 'my_notes',
  ONE_TO_ONE = 'one_to_one',
  GROUP = 'group',
}

/**
 * Chat Member Role enum
 * - member: Regular participant
 * - admin: Can manage members (add/remove)
 * - owner: Creator, full permissions, cannot be removed
 */
export enum ChatMemberRole {
  MEMBER = 'member',
  ADMIN = 'admin',
  OWNER = 'owner',
}

/**
 * Chat Member with user data
 */
export interface ChatMember {
  user_id: string;
  role: ChatMemberRole;
  display_name: string;
  email: string;
  avatar_url: string | null;
  joined_at: string;
}

export interface Chat {
  id: string;
  tenant_id: string;

  // Legacy instance-based fields (backward compatible)
  instance_type: string;
  instance_id: string;

  // New chat types system
  chat_type?: ChatType;
  is_general?: boolean;

  title: string;
  description?: string;

  // Members (only included in detail view)
  members?: ChatMember[];
  my_role?: ChatMemberRole | null;
  participant_count?: number;

  created_at: string;
  created_by?: string;
  updated_at: string;
  updated_by?: string;
  is_active: boolean;
}

export interface ChatCreate {
  // Legacy instance-based creation (backward compatible)
  instance_type?: string;
  instance_id?: string;

  // New chat types creation
  chat_type?: ChatType;
  participant_ids?: string[];

  title: string;
  description?: string;
}

export interface ChatUpdate {
  title?: string;
  description?: string;
}

/**
 * Request to find or create 1-1 chat
 */
export interface FindOrCreateOneToOneRequest {
  other_user_id: string;
}

/**
 * Request to add members to group chat
 */
export interface AddMembersRequest {
  user_ids: string[];
}

/**
 * Request to update member role
 */
export interface UpdateMemberRoleRequest {
  role: ChatMemberRole.ADMIN | ChatMemberRole.MEMBER;
}

// ============================================================================
// Message Types
// ============================================================================

/**
 * Message delivery status
 * - sending: Message is being sent to backend
 * - sent: Message received by backend (REST API response)
 * - delivered: Message confirmed via WebSocket event
 * - failed: Message failed to send
 */
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'failed';

/**
 * User information included in chat messages
 */
export interface ChatUser {
  id: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
}

export interface Message {
  id: string;
  tenant_id: string;
  chat_id: string;
  parent_message_id?: string;
  message: string;
  has_attachments: boolean;
  attachments_metadata?: {
    files: Array<{
      id: string;
      filename: string;
      size: number;
      mime_type: string;
      url?: string;
    }>;
  };
  archived_at?: string;
  archived_by?: string;
  created_at: string;
  created_by?: string;
  created_by_user?: ChatUser | null;
  updated_at: string;
  // Frontend-only fields for optimistic updates
  status?: MessageStatus;
  isTempMessage?: boolean;
  updated_by?: string;
  is_active: boolean;
  is_edited?: boolean;
}

export interface MessageCreate {
  message: string;
  parent_message_id?: string;
  has_attachments?: boolean;
  mentioned_user_ids?: string[];
  attachment_ids?: string[];
}

export interface MessageUpdate {
  message: string;
}

// ============================================================================
// Reaction/Like Types
// ============================================================================

export type LikeType =
  | 'thumbs_up'
  | 'thumbs_down'
  | 'heart'
  | 'laugh'
  | 'surprised'
  | 'sad'
  | 'angry';

export interface MessageLike {
  id: string;
  message_id: string;
  like_type: LikeType;
  created_by?: string;
  created_by_user?: ChatUser | null;
  created_at: string;
}

export interface MessageLikeCreate {
  like_type: LikeType;
}

// ============================================================================
// Read Receipt Types
// ============================================================================

export interface MessageSeen {
  id: string;
  message_id: string;
  created_by?: string;
  created_at: string;
}

// ============================================================================
// Mention Types
// ============================================================================

export interface MessageMention {
  id: string;
  message_id: string;
  mentioned_user_id: string;
  created_at: string;
}

export interface MyMention {
  message_id: string;
  message: string;
  mentioned_at: string;
  mentioned_by: string;
}

// ============================================================================
// Subscription Types
// ============================================================================

export type SubscriptionType =
  | 'INSTANT'
  | 'HOURLY_DIGEST'
  | 'DAILY_DIGEST'
  | 'WEEKLY_DIGEST';

export type TargetType = 'EMAIL' | 'TELEGRAM';

export interface ChatSubscription {
  id: string;
  tenant_id: string;
  chat_id: string;
  subscriber_user_id: string;
  subscription_type: SubscriptionType;
  target_type: TargetType;
  target: string;
  is_active: boolean;
  created_at: string;
  created_by?: string;
  updated_at: string;
  updated_by?: string;
}

export interface SubscriptionCreate {
  subscription_type: SubscriptionType;
  target_type: TargetType;
  is_active?: boolean;
}

export interface SubscriptionUpdate {
  subscription_type?: SubscriptionType;
  target_type?: TargetType;
  is_active?: boolean;
}

export type DeliveryStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface NotificationDelivery {
  id: string;
  subscription_id: string;
  sent_at: string;
  target: string;
  message_content: string;
  message_count: number;
  delivery_status: DeliveryStatus;
  error_message?: string;
  created_at: string;
}

// ============================================================================
// WebSocket Event Types
// ============================================================================

export interface WebSocketEvent<T = any> {
  type: string;
  tenant_id: string;
  timestamp: string;
  user_id?: string;
  data: T;
}

export interface ChatMessageCreatedEvent extends WebSocketEvent {
  type: 'chat.message.created';
  data: {
    chat_id: string;
    message_id: string;
    message: string;
    created_by: string;
    created_by_user?: ChatUser | null;
    parent_message_id?: string;
    has_attachments: boolean;
    attachments_metadata?: {
      files: Array<{
        id: string;
        filename: string;
        size: number;
        mime_type: string;
        url?: string;
      }>;
    };
    mentioned_user_ids: string[];
    created_at: string;
  };
}

export interface ChatMessageUpdatedEvent extends WebSocketEvent {
  type: 'chat.message.updated';
  data: {
    chat_id: string;
    message_id: string;
    message: string;
    created_by_user?: ChatUser | null;
  };
}

export interface ChatMessageArchivedEvent extends WebSocketEvent {
  type: 'chat.message.archived';
  data: {
    chat_id: string;
    message_id: string;
  };
}

export interface ChatLikeAddedEvent extends WebSocketEvent {
  type: 'chat.like.added';
  data: {
    chat_id: string;
    message_id: string;
    like_id: string;
    like_type: LikeType;
    created_by: string;
    created_by_user?: ChatUser | null;
  };
}

export interface ChatLikeRemovedEvent extends WebSocketEvent {
  type: 'chat.like.removed';
  data: {
    chat_id: string;
    message_id: string;
    like_id: string;
  };
}

export interface ChatMessageSeenEvent extends WebSocketEvent {
  type: 'chat.message.seen';
  data: {
    chat_id: string;
    message_id: string;
    seen_by: string;
    seen_at: string;
  };
}

/**
 * WebSocket event when member is added to chat
 */
export interface ChatMemberAddedEvent extends WebSocketEvent {
  type: 'chat.member.added';
  data: {
    chat_id: string;
    user_id: string;
    display_name: string;
    role: ChatMemberRole;
    added_by: string;
    added_by_name: string;
  };
}

/**
 * WebSocket event when member is removed from chat
 */
export interface ChatMemberRemovedEvent extends WebSocketEvent {
  type: 'chat.member.removed';
  data: {
    chat_id: string;
    user_id: string;
    removed_by: string;
    removed_by_name: string;
  };
}

/**
 * WebSocket event when member role is changed
 */
export interface ChatMemberRoleChangedEvent extends WebSocketEvent {
  type: 'chat.member.role_changed';
  data: {
    chat_id: string;
    user_id: string;
    old_role: ChatMemberRole;
    new_role: ChatMemberRole;
    changed_by: string;
    changed_by_name: string;
  };
}

// Union type for all chat events
export type ChatEvent =
  | ChatMessageCreatedEvent
  | ChatMessageUpdatedEvent
  | ChatMessageArchivedEvent
  | ChatLikeAddedEvent
  | ChatLikeRemovedEvent
  | ChatMessageSeenEvent
  | ChatMemberAddedEvent
  | ChatMemberRemovedEvent
  | ChatMemberRoleChangedEvent;
